import React, { useState, useRef } from 'react';
import { ResultadoImportacion } from '../tipos/vacaciones';
import { importarTxt } from '../services/periodoService';
import './ImportarPeriodos.css';

const EJEMPLO_TXT = `# Formato: Email;TipoLicencia;FechaInicio;FechaFin;Descripcion(opcional)
# Tipos válidos: VAC, LIC_MED, DIA_BEN, COMP
# Fechas en formato DD/MM/YYYY
# Las líneas que comienzan con # son comentarios y se ignoran

juan@company.com;VAC;01/07/2026;15/07/2026;Vacaciones de invierno
maria@company.com;LIC_MED;10/07/2026;12/07/2026;Control médico
pedro@company.com;DIA_BEN;05/07/2026;05/07/2026
juan@company.com;COMP;20/07/2026;22/07/2026;Compensatorio junio`;

const ImportarPeriodos: React.FC<{ onDatosActualizados: () => void }> = ({ onDatosActualizados }) => {
  const [archivo, setArchivo] = useState<File | null>(null);
  const [cargando, setCargando] = useState(false);
  const [resultado, setResultado] = useState<ResultadoImportacion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const descargarEjemplo = () => {
    const blob = new Blob([EJEMPLO_TXT], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ejemplo_importacion.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const manejarArchivo = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null;
    setArchivo(f);
    setResultado(null);
    setError(null);
  };

  const importar = async () => {
    if (!archivo) return;
    setCargando(true);
    setError(null);
    setResultado(null);
    try {
      const res = await importarTxt(archivo);
      setResultado(res);
      if (res.exitosos > 0) onDatosActualizados();
    } catch (e: any) {
      setError(e.message || 'Error al importar');
    } finally {
      setCargando(false);
    }
  };

  const descargarErrores = () => {
    if (!resultado) return;
    const fallidas = resultado.resultados.filter(r => !r.exitoso && !r.omitido);
    const lineas = [
      '# Filas con error — corregalas y volvé a importar este archivo',
      '# Formato: Email;TipoLicencia;FechaInicio;FechaFin;Descripcion(opcional)',
      '',
      ...fallidas.flatMap(f => [
        `# ERROR: ${f.error}`,
        f.contenido,
      ]),
    ];
    const blob = new Blob([lineas.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'errores_importacion.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const limpiar = () => {
    setArchivo(null);
    setResultado(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="importar-container">
      <div className="importar-header">
        <h2>Importar períodos desde archivo TXT</h2>
        <p className="importar-subtitulo">
          Cargá múltiples períodos de vacaciones o licencias de una sola vez usando un archivo de texto.
        </p>
      </div>

      <div className="importar-formato">
        <h3><i className="fas fa-info-circle"></i> Formato del archivo</h3>
        <div className="formato-descripcion">
          <p>Cada línea debe tener los campos separados por <code>;</code>:</p>
          <div className="formato-campos">
            <span className="campo-requerido">Email del empleado</span>
            <span className="separador">;</span>
            <span className="campo-requerido">Tipo (VAC / LIC_MED / DIA_BEN / COMP)</span>
            <span className="separador">;</span>
            <span className="campo-requerido">Fecha inicio (DD/MM/YYYY)</span>
            <span className="separador">;</span>
            <span className="campo-requerido">Fecha fin (DD/MM/YYYY)</span>
            <span className="separador">;</span>
            <span className="campo-opcional">Descripción (opcional)</span>
          </div>
          <pre className="formato-ejemplo">
{`# Comentarios comienzan con #
juan@company.com;VAC;01/07/2026;15/07/2026;Vacaciones de invierno
maria@company.com;LIC_MED;10/07/2026;12/07/2026
pedro@company.com;DIA_BEN;05/07/2026;05/07/2026`}
          </pre>
        </div>
        <button className="btn-descargar-ejemplo" onClick={descargarEjemplo}>
          <i className="fas fa-download"></i> Descargar archivo de ejemplo
        </button>
      </div>

      <div className="importar-upload">
        <h3><i className="fas fa-upload"></i> Seleccionar archivo</h3>
        <div className="upload-area">
          <input
            ref={inputRef}
            type="file"
            accept=".txt"
            onChange={manejarArchivo}
            className="file-input"
            id="file-input"
          />
          <label htmlFor="file-input" className="file-label">
            <i className="fas fa-file-alt"></i>
            {archivo ? archivo.name : 'Elegir archivo .txt'}
          </label>
          {archivo && (
            <div className="file-info">
              <i className="fas fa-check-circle"></i>
              {archivo.name} ({(archivo.size / 1024).toFixed(1)} KB)
            </div>
          )}
        </div>
        <div className="upload-actions">
          <button
            className="btn-importar"
            onClick={importar}
            disabled={!archivo || cargando}
          >
            {cargando ? (
              <><i className="fas fa-spinner fa-spin"></i> Importando...</>
            ) : (
              <><i className="fas fa-file-import"></i> Importar</>
            )}
          </button>
          {(archivo || resultado) && (
            <button className="btn-limpiar" onClick={limpiar}>
              <i className="fas fa-times"></i> Limpiar
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="importar-error">
          <i className="fas fa-exclamation-triangle"></i> {error}
        </div>
      )}

      {resultado && (
        <div className="importar-resultados">
          <div className="resultados-resumen">
            <div className="resumen-card total">
              <span className="resumen-numero">{resultado.totalLineas}</span>
              <span className="resumen-label">Total procesadas</span>
            </div>
            <div className="resumen-card exitoso">
              <span className="resumen-numero">{resultado.exitosos}</span>
              <span className="resumen-label">Importadas</span>
            </div>
            <div className="resumen-card omitido">
              <span className="resumen-numero">{resultado.omitidos}</span>
              <span className="resumen-label">Ya existían</span>
            </div>
            <div className="resumen-card fallido">
              <span className="resumen-numero">{resultado.fallidos}</span>
              <span className="resumen-label">Con errores</span>
            </div>
          </div>

          {resultado.fallidos > 0 && (
            <div className="resultados-acciones">
              <button className="btn-descargar-errores" onClick={descargarErrores}>
                <i className="fas fa-download"></i> Descargar filas con error ({resultado.fallidos})
              </button>
              <span className="acciones-ayuda">
                Corregí el archivo descargado y volvé a importarlo.
              </span>
            </div>
          )}

          <table className="resultados-tabla">
            <thead>
              <tr>
                <th>#</th>
                <th>Estado</th>
                <th>Contenido</th>
                <th>Detalle</th>
              </tr>
            </thead>
            <tbody>
              {resultado.resultados.map((fila) => (
                <tr key={fila.fila} className={fila.exitoso ? 'fila-ok' : fila.omitido ? 'fila-omitida' : 'fila-error'}>
                  <td>{fila.fila}</td>
                  <td>
                    {fila.exitoso
                      ? <span className="badge-ok"><i className="fas fa-check"></i> OK</span>
                      : fila.omitido
                        ? <span className="badge-omitido"><i className="fas fa-minus"></i> Ya existía</span>
                        : <span className="badge-error"><i className="fas fa-times"></i> Error</span>
                    }
                  </td>
                  <td className="celda-contenido">{fila.contenido}</td>
                  <td>
                    {fila.exitoso && fila.periodo
                      ? <span className="detalle-ok">{fila.periodo.nombreEmpleado} — {fila.periodo.dias} días hábiles</span>
                      : fila.omitido
                        ? <span className="detalle-omitido">{fila.omitidoRazon}</span>
                        : <span className="detalle-error">{fila.error}</span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ImportarPeriodos;
